import { alpha, createTheme } from '@mui/material/styles';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { pickerDayClasses } from '@mui/x-date-pickers/PickerDay';

export const PRIMARY_LIGHT  = '#2243d0'; // was #3E5CFF — 7.55:1 on white ✓ AAA
const PRIMARY_DARK   = '#a8b4ff'; // was #7E94FF — 7.68:1 on dark paper #292524 ✓ AAA
export const SUCCESS      = '#16a34a';
export const ERROR        = '#991b1b'; // red-800 — 8.3:1 on white ✓ AAA
export const ERROR_DARK   = '#fca5a5'; // red-300 — 8.0:1 on dark paper #292524 ✓ AAA
export const WARNING      = '#d97706';

export const theme = createTheme({
  cssVariables: {
    colorSchemeSelector: 'class',
  },
  colorSchemes: {
    light: {
      palette: {
        primary: {
          main: PRIMARY_LIGHT,
          dark: '#1a2db0',
          light: '#6b84e8',
          contrastText: '#ffffff',
        },
        success: { main: SUCCESS, contrastText: '#ffffff' },
        error:   { main: ERROR,   contrastText: '#ffffff' },
        warning: { main: WARNING, contrastText: '#ffffff' },
        background: { default: '#fafaf9', paper: '#ffffff' },
        text: { primary: '#1c1917', secondary: '#4a4a4a', disabled: '#595959' },
      },
    },
    dark: {
      palette: {
        primary: {
          main: PRIMARY_DARK,
          dark: PRIMARY_LIGHT,
          light: '#c4ccff',
          contrastText: '#1c1917',
        },
        success: { main: SUCCESS,    contrastText: '#ffffff' },
        error:   { main: ERROR_DARK, contrastText: '#1c1917' },
        warning: { main: WARNING,    contrastText: '#ffffff' },
        background: { default: '#1c1917', paper: '#292524' },
        text: { primary: '#fafaf9', secondary: '#d6d3d1', disabled: '#bdb6b2' },
      },
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: { borderRadius: 10 },
  components: {
    MuiIconButton: {
      styleOverrides: {
        root: ({ ownerState, theme: t }) => {
          const color = ownerState.color ?? 'default';
          if (color === 'inherit') return {};

          const lightMap: Record<string, string> = {
            primary: PRIMARY_LIGHT, default: PRIMARY_LIGHT,
            error: ERROR, success: SUCCESS, warning: WARNING,
          };
          const darkMap: Record<string, string> = {
            primary: PRIMARY_DARK, default: PRIMARY_DARK,
            error: ERROR_DARK, success: SUCCESS, warning: WARNING,
          };

          const lc = lightMap[color] ?? PRIMARY_LIGHT;
          const dc = darkMap[color] ?? PRIMARY_DARK;
          const isNamedColor = color === 'primary' || color === 'default';

          return {
            backgroundColor: alpha(lc, 0.08),
            ...(isNamedColor && { color: lc }),
            '&:hover': { backgroundColor: alpha(lc, 0.14) },
            ...t.applyStyles('dark', {
              backgroundColor: alpha(dc, 0.13),
              ...(isNamedColor && { color: dc }),
              '&:hover': { backgroundColor: alpha(dc, 0.20) },
            }),
          };
        },
      },
    },
    MuiPickerDay: {
      styleOverrides: {
        root: {
          [`&.${pickerDayClasses.disabled}`]: {
            textDecoration: 'line-through',
            textDecorationColor: 'currentColor',
          },
        },
      },
    },
    MuiMultiSectionDigitalClockSection: {
      styleOverrides: {
        item: ({ theme: t }) => ({
          '&.Mui-disabled': {
            opacity: 1,
            color: (t.vars || t).palette.text.disabled,
            textDecoration: 'line-through',
            textDecorationColor: 'currentColor',
          },
        }),
      },
    },
    MuiDigitalClock: {
      styleOverrides: {
        item: ({ theme: t }) => ({
          '&.Mui-disabled': {
            opacity: 1,
            color: (t.vars || t).palette.text.disabled,
            textDecoration: 'line-through',
            textDecorationColor: 'currentColor',
          },
        }),
      },
    },
  },
});
